import {
  Component,
  effect,
  inject,
  Input,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { CategoryStore } from '../../../stores/category/category.state';
import {
  PickList,
  PickListModule,
  PickListSourceSelectEvent,
} from 'primeng/picklist';
import { Indicator } from '../../../models/indicator';
import { NgClass, NgForOf, NgIf, SlicePipe } from '@angular/common';
import { IndicatorFilterComponent } from '../indicator-filter/indicator-filter.component';
import { DividerModule } from 'primeng/divider';
import { TagListComponent } from '../tag-list/tag-list.component';
import { buildSortedIndicatorList } from '../../../stores/indicator/indicator.selectors';
import { ChipModule } from 'primeng/chip';
import { BadgeModule } from 'primeng/badge';
import { units } from '../../../models/unit';
import { StyleClassModule } from 'primeng/styleclass';
import { TagModule } from 'primeng/tag';
import { TrimPipe } from '../../../pipes/trim.pipe';
import { TooltipModule } from 'primeng/tooltip';
import { Access } from '../../../models/rbac';
import { PermissionService } from '../../../services/permission.service';

@Component({
  selector: 'app-indicator-pick-list',
  standalone: true,
  imports: [
    PickListModule,
    NgIf,
    NgClass,
    IndicatorFilterComponent,
    DividerModule,
    TagListComponent,
    ChipModule,
    NgForOf,
    BadgeModule,
    StyleClassModule,
    TagModule,
    SlicePipe,
    TrimPipe,
    TooltipModule,
  ],
  templateUrl: './indicator-pick-list.component.html',
  styleUrl: './indicator-pick-list.component.scss',
})
export class IndicatorPickListComponent implements OnDestroy {
  @ViewChild('listPickerCtrl') listPickerCtrl!: PickList;
  @Input() ignoreChildren = false;
  @Input() targetHeaderSubtitle?: string;
  @Input() showSourceControls = false;
  @Input() showTargetControls = true;
  @Input() autoSelectSet = true;
  @Input() autoSelectChildren = false;
  @Input() keepSelection = false;
  @Input() action?: keyof Access;

  readonly indicatorStore = inject(IndicatorStore);
  readonly categoryStore = inject(CategoryStore);

  selected: Indicator[] = [];
  sortedIndicators: Indicator[] = [];

  constructor(private permissionService: PermissionService) {
    effect(() => {
      this.selected = buildSortedIndicatorList(
        this.categoryStore.asArray(),
        this.indicatorStore.filteredSelection(),
        this.ignoreChildren ? {} : this.indicatorStore.groupedChildren(),
        this.indicatorStore.onlySets()
      );
    });
  }

  /**
   * Retrieves a list of indicators based on the current action.
   * If the action is 'export', it returns indicators with export rights.
   * Otherwise, it returns the filtered list of indicators from the store.
   *
   * @return {Indicator[]} A list of indicators based on the specified conditions.
   */
  getIndicators(): Indicator[] {
    return this.action === 'export'
      ? this.permissionService.indicatorsWithExportRights()
      : this.indicatorStore.filteredItems();
  }

  /**
   * Updates the current selection within the indicator store with the selected items.
   * If auto selection of parent items is enabled, parents of the selected items
   * are added to the selection. Similarly, if auto selection of children items
   * is enabled, children of the selected items are added to the selection.
   *
   * @return {void} This method does not return a value.
   */
  updateSelection(): void {
    this.indicatorStore.updateSelection(this.selected);

    if (this.autoSelectSet) {
      this.indicatorStore.addParentsToSelection();
      this.selected = this.indicatorStore.selection();
    }

    if (this.autoSelectChildren) {
      this.indicatorStore.addChildrenToSelection();
      this.selected = this.indicatorStore.selection();
    }
  }

  /**
   * Handles checking for and processing child elements of selected items in the pick list.
   * This method adds parent items to the selected items source if they are not already included.
   *
   * @param {PickListSourceSelectEvent} event - The event object containing the selected items from the source pick list.
   * @return {void} Does not return a value.
   */
  checkForSelectionHooks(event: PickListSourceSelectEvent): void {
    if (this.autoSelectSet) {
      event.items.forEach((indicator: Indicator) => {
        this.selectParent(indicator);
      });
    }

    if (this.autoSelectChildren) {
      event.items.forEach((indicator: Indicator) => {
        this.selectChildren(indicator);
      });
    }
  }

  /**
   * Selects and adds the parent indicator to the list of selected items,
   * if the parent exists and is not already selected.
   *
   * @param {Indicator} indicator - The indicator whose parent needs to be selected.
   * @return {void}
   */
  private selectParent(indicator: Indicator): void {
    if (indicator.parent) {
      const parent = this.indicatorStore.getById(indicator.parent);
      if (this.listPickerCtrl.selectedItemsSource.includes(parent)) return;
      this.listPickerCtrl.selectedItemsSource.push(parent);
    }
  }

  /**
   * Selects and updates the children of a given indicator into the current list picker controller's selected items source.
   *
   * @param {Indicator} indicator - The indicator whose children will be selected and added to the selected items source. This must have the `isSet` property set to true to proceed.
   * @return {void} This method does not return a value.
   */
  private selectChildren(indicator: Indicator): void {
    if (!indicator.isSet) return;
    const uniqueItems = Array.from(
      new Set([
        ...this.listPickerCtrl.selectedItemsSource,
        ...this.indicatorStore.getChildren(indicator._id),
      ])
    );
    this.listPickerCtrl.selectedItemsSource = uniqueItems;
  }

  /**
   * Sorts and filters a list of indicators based on the provided category, indicator, and children data.
   * The method constructs a sorted list of indicators and excludes those that are part of the selected indicators.
   * It also considers whether to ignore child grouping based on the `ignoreChildren` flag.
   *
   * @return {void} Does not return a value. The sorted and filtered list is stored internally in the `sortedIndicators` property.
   */
  sortedByTree(): void {
    this.sortedIndicators = buildSortedIndicatorList(
      this.categoryStore.asArray(),
      this.getIndicators(),
      this.ignoreChildren ? {} : this.indicatorStore.groupedChildren(),
      this.indicatorStore.onlySets()
    ).filter(indicator => {
      return !this.selected.includes(indicator);
    });
  }

  /**
   * Getter method that returns an array of indicators sorted based on specific tree-like logic.
   * This method internally calls `sortedByTree` to apply the sort mechanism.
   *
   * @return {Indicator[]} An array of sorted indicators.
   */
  get treeSorted(): Indicator[] {
    this.sortedByTree();
    return this.sortedIndicators;
  }

  /**
   * Lifecycle hook that is invoked when the component or directive instance is being destroyed.
   * This method is used to perform cleanup operations such as unsubscribing from observables,
   * detaching event handlers, or clearing data stored in services.
   *
   * @return {void} This method does not return any value.
   */
  ngOnDestroy(): void {
    if (!this.keepSelection) this.indicatorStore.clearSelection();
  }

  protected readonly units = units;
}
